import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { inrFull, inr, ftime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { mobileError, normalizeMobile } from "@/lib/phone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { InvoiceActions } from "@/components/pawkt/InvoiceActions";
import { ContextPanel } from "@/components/pawkt/ContextPanel";
import { CoverageBadge } from "@/components/pawkt/PackageCoverage";
import { unitNoun, pkgCategory } from "@/lib/packageUnit";
import { DiscardInvoiceButton } from "@/components/pawkt/DiscardInvoiceModal";
import { PawPrint } from "lucide-react";
import { EncounterNotice } from "@/components/pawkt/EncounterNotice";
import { CollectAllocation } from "@/components/pos/CollectAllocation";
import { cashBreakdown, CashChange, toP } from "@/components/pos/cashChange";

const localDay = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
// Pre-bill estimate shown on appointment cards: services + booking products (catalog price × qty).
const estBookingAmount = (b) => [...(b.services || []), ...(b.products || [])]
  .reduce((t, s) => t + (s.price || 0) * (parseInt(s.qty, 10) || 1), 0);
// Mirrors ops.BACKDATE_MAX_DAYS (how far back an appointment may be created).
const BACKDATE_WINDOW_DAYS = 7;
import {
  Plus, Minus, Trash2, ShoppingCart, Scissors, Package, Search, X, Crown, Layers, ChevronDown, ChevronUp,
  Receipt, Printer, Download, CheckCircle2, AlertCircle, Building2, UserPlus, IndianRupee, Pencil, Check,
} from "lucide-react";
import { toast } from "sonner";
import { AddressPicker, addressError } from "@/components/pawkt/AddressPicker";
import { PetHealthFields, petHealthError, petHealthPayload } from "@/components/pawkt/VaccinationRecord";
import { PetAgeField } from "@/components/pawkt/PetAgeField";
import { petAgeError } from "@/lib/petAge";

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const METHODS = ["cash", "upi", "card", "online", "bank"];
// "online" is a MANUAL (counter-recorded) method — NOT the Razorpay gateway. Labelled
// explicitly so staff don't confuse it with the "Pay Online (Razorpay)" gateway action.
const METHOD_LABELS = { cash: "Cash", upi: "UPI", card: "Card", online: "Other", bank: "Bank Transfer" };
// POS "Record Payment" manual methods. The backend keeps its existing method values
// (cash/upi/card/bank/online); "online" is reused as the generic "Other" bucket and is
// NEVER shown to staff as the word "Online" — this removes the Pay-Online vs Online ambiguity.
const RECORD_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "bank", label: "Bank Transfer" },
  { value: "online", label: "Other" },
];

export default function POS() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const canDiscount =
    ["owner", "admin", "manager"].includes(user?.role) ||
    (user?.permissions || []).includes("*") ||
    (user?.permissions || []).includes("discount.manual");
  const canManual = hasPerm(user, "invoices.manual_charge");
  const tenantWide = !(user?.branch_ids && user.branch_ids.length);

  const [branchId, setBranchId] = useState("");
  const [custQuery, setCustQuery] = useState("");
  const [catQuery, setCatQuery] = useState("");
  const [customer, setCustomer] = useState(null);
  const [selectedPets, setSelectedPets] = useState([]); // multi-pet (walk-in) selection
  const petId = selectedPets.length === 1 ? selectedPets[0] : ""; // invoice-level pet (single-pet only)
  const [bookingId, setBookingId] = useState("");
  const [encounterId, setEncounterId] = useState("");
  const [encNote, setEncNote] = useState(null);
  const [cart, setCart] = useState([]);
  const [manual, setManual] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountReason, setDiscountReason] = useState("");
  const [coupon, setCoupon] = useState("");
  const [payments, setPayments] = useState([{ method: "cash", amount: "" }]);
  const [splitMode, setSplitMode] = useState(false);
  const [payDate, setPayDate] = useState(localDay(new Date()));
  const [backdateReason, setBackdateReason] = useState("");
  const canBackdate = hasPerm(user, "payments.backdate");
  const [preview, setPreview] = useState(null);
  const [gstOn, setGstOn] = useState(true);
  const [gstRate, setGstRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [collectInvoice, setCollectInvoice] = useState(null);
  const [collectLines, setCollectLines] = useState([]);
  const collectIdem = useRef("");
  const [apptDate, setApptDate] = useState(localDay(new Date()));
  const [onlineBusy, setOnlineBusy] = useState(false);
  const [newCust, setNewCust] = useState(null); // {name,mobile} when creating
  const [csStandalone, setCsStandalone] = useState(false);
  const [newPet, setNewPet] = useState(null);
  const [panelCtx, setPanelCtx] = useState(false);
  const [genBusy, setGenBusy] = useState(null);
  const [editBk, setEditBk] = useState(null);
  const [cancelBk, setCancelBk] = useState(null);
  const idemRef = useRef(uuid());

  const { data: services } = useQuery({ queryKey: ["services", branchId], queryFn: async () => ((await api.get("/services")).data || []).filter((s) => s.active !== false && !(s.vaccine_catalog_id && !(Number(s.price) > 0))) });
  const svcCat = Object.fromEntries((services || []).map((s) => [s.id, s.category]));
  const { data: products } = useQuery({ queryKey: ["products", branchId], queryFn: async () => (await api.get("/products")).data });
  const { data: staff } = useQuery({ queryKey: ["staff-lite"], queryFn: async () => (await api.get("/staff")).data });
  const { data: gateway } = useQuery({ queryKey: ["gateway-status"], queryFn: async () => (await api.get("/payments/gateway-status")).data });
  const razorpayOn = !!gateway?.razorpay?.connected;
  const { data: allBranches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  // only branches this user may sell for; a single-branch business needs no selector (server uses its only branch)
  const branches = (allBranches || []).filter((b) => tenantWide || user.branch_ids.includes(b.id));
  const multiBranch = (allBranches?.length || 0) > 1;
  useEffect(() => { if (multiBranch && !branchId && branches.length === 1) setBranchId(branches[0].id); }, [multiBranch, branchId, branches]);
  // walk-in bills in a multi-branch business need the sale branch (server enforces it too); bring the selector into view
  const needSaleBranch = () => {
    if (!multiBranch || branchId || bookingId || encounterId) return false;
    toast.error("Choose the branch where this sale was made.");
    document.querySelector('[data-testid="pos-branch"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  };
  const { data: todayBookings } = useQuery({
    queryKey: ["today-bookings", branchId, apptDate],
    queryFn: async () => (await api.get(`/bookings?start=${apptDate}T00:00:00&end=${apptDate}T23:59:59&limit=200${branchId ? `&branch=${branchId}` : ""}`)).data,
  });
  // Earlier (backdated-window) appointments still needing a bill or payment.
  const { data: earlierBookings } = useQuery({
    queryKey: ["today-bookings", "earlier", branchId],
    queryFn: async () => {
      const s = new Date(); s.setDate(s.getDate() - BACKDATE_WINDOW_DAYS);
      const y = new Date(); y.setDate(y.getDate() - 1);
      return (await api.get(`/bookings?start=${localDay(s)}T00:00:00&end=${localDay(y)}T23:59:59&limit=500${branchId ? `&branch=${branchId}` : ""}`)).data;
    },
  });
  const { data: custSearch } = useQuery({
    queryKey: ["pos-cust", custQuery],
    queryFn: async () => (await api.get(`/customers?search=${encodeURIComponent(custQuery)}&limit=8`)).data,
    enabled: custQuery.length >= 2 && !customer,
  });
  const { data: ctx } = useQuery({
    queryKey: ["pos-ctx", customer?.id],
    queryFn: async () => (await api.get(`/pos/customer/${customer.id}`)).data,
    enabled: !!customer,
  });
  const { data: taxOptions } = useQuery({
    queryKey: ["pos-tax-options"],
    queryFn: async () => (await api.get(`/pos/tax-options`)).data,
  });

  // SOURCE OF TRUTH for the loaded booking. React Query keys on bookingId, so a newer
  // selection (A→B) supersedes an in-flight older one (out-of-order protection) and a
  // booking edit that invalidates ["pos-booking"] auto-refetches → the reconcile effect
  // below re-syncs the cart with NO page refresh. Disabled in collect mode (no cart).
  const { data: selBooking } = useQuery({
    queryKey: ["pos-booking", bookingId],
    queryFn: async () => (await api.get(`/bookings/${bookingId}`)).data,
    enabled: !!bookingId && !collectInvoice,
  });

  // Preload a booking arriving from a deep link (?booking=…). Sets identity only; the
  // authoritative query + reconcile effect hydrate the cart. Already-billed → collect mode.
  useEffect(() => {
    const bid = params.get("booking");
    if (!bid) return;
    (async () => {
      try {
        const b = (await api.get(`/bookings/${bid}`)).data;
        if (!b) return;
        const c = (await api.get(`/customers/${b.customer_id}`)).data;
        setCustomer(c); setSelectedPets(b.pet_id ? [b.pet_id] : []);
        if (b.branch_id) setBranchId(b.branch_id);
        const bill = b.billing || {};
        if (bill.invoice_id && (bill.state === "UNPAID" || bill.state === "PARTIALLY_PAID")) {
          try {
            const inv = (await api.get(`/invoices/${bill.invoice_id}`)).data;
            setCollectInvoice(inv); setCollectLines([{ method: "upi", amount: inv.balance }]); collectIdem.current = uuid();
          } catch { toast.error("Could not load the invoice to collect"); }
          return;
        }
        if (bill.state === "PAID") { toast.info("This booking is already fully paid."); return; }
        setBookingId(b.id);
      } catch {}
    })();
  }, [params]);

  // Deep link from the consultation billing queue (?encounter=…): prefill customer/pet, the linked
  // booking (existing reconcile adds its services) and the dispensed medication products.
  const encLoaded = useRef("");
  const bkEncChecked = useRef("");
  const encBooking = useRef("");
  const clearEncounter = () => {
    encLoaded.current = ""; bkEncChecked.current = ""; encBooking.current = "";
    setEncounterId(""); setEncNote(null); setCart((prev) => prev.filter((l) => l.source !== "encounter"));
  };
  const loadEncounter = async (eid, refresh = false) => {
    encLoaded.current = eid;
      try {
        const d = (await api.get(`/veterinary/encounters/${eid}/bill-draft`)).data;
        if (d.booking_id) bkEncChecked.current = d.booking_id;
        encBooking.current = d.booking_id || "";
        if (!d.encounter_billable && !d.booking_unbilled) { setEncNote({ missing: [], notBillable: [], billed: (d.clinical || []).filter((x) => x.billed), stock: [] }); toast.info("This consultation is already billed."); return; }
        const c = (await api.get(`/customers/${d.customer_id}`)).data;
        setCustomer(c); setSelectedPets(d.pet_id ? [d.pet_id] : []);
        if (d.branch_id) setBranchId(d.branch_id);
        if (d.booking_id && d.booking_unbilled) setBookingId(d.booking_id);
        if (d.encounter_billable) setEncounterId(eid);
        const meds = d.encounter_billable ? d.products_to_bill : [];
        const clin = (d.clinical_to_bill || []).map((x) => ({
          _k: `enc-cl-${x.entry_id}`, kind: "service", ref_id: x.service_id, name: `${x.clinical_name && x.clinical_name !== x.name ? `${x.clinical_name} — ` : ""}${x.name} (from vet consultation)`, qty: 1,
          price: x.price, tax_rate: x.tax_rate ?? 18, staff_id: "", pet_id: d.pet_id || "", source: "encounter", source_entry_id: x.entry_id,
        }));
        const stock = d.encounter_billable ? (d.dispensed || []).filter((x) => x.qty > x.billed_qty && (x.missing_product || x.insufficient_stock || !(Number(x.price) > 0))) : [];
        setEncNote({ eid, missing: d.clinical_missing_price || [], notBillable: d.clinical_not_billable || [], billed: (d.clinical || []).filter((x) => x.billed), stock,
          rx: (d.prescriptions || []).filter((p) => p.dispense_status !== "dispensed") });
        if (stock.length) toast.warning(`Check dispensed medicines: ${stock.map((x) => x.name).join(", ")}`, { duration: 9000 });
        if ((d.clinical_missing_price || []).length) toast.warning(`No catalogue price for: ${d.clinical_missing_price.map((x) => x.clinical_name).join(", ")}. Ask the vet to link a priced service, or add it manually.`, { duration: 9000 });
        if ((d.clinical_not_billable || []).length) toast.info(`Not billed (not administered/completed): ${d.clinical_not_billable.map((x) => x.name).join(", ")}`, { duration: 7000 });
        const fees = (d.services_to_bill || []).map((s) => ({
          _k: `enc-svc-${s.service_id}`, kind: "service", ref_id: s.service_id, name: s.service_name, qty: 1,
          price: s.price, tax_rate: 18, staff_id: "", pet_id: d.pet_id || "", source: "encounter",
        }));
        setCart((prev) => [...prev.filter((l) => l.source !== "encounter"), ...fees, ...clin, ...meds.map((p) => ({
          _k: `enc-prd-${p.product_id}`, kind: "product", ref_id: p.product_id, name: p.name, qty: p.qty,
          price: p.price, tax_rate: p.tax_rate ?? 18, staff_id: "", pet_id: d.pet_id || "", source: "encounter",
        }))]);
        toast.success(refresh ? "Consultation items refreshed" : `Consultation for ${d.pet_name || "pet"} loaded for billing`);
      } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    const eid = params.get("encounter");
    if (!eid || encLoaded.current === eid) return;
    loadEncounter(eid);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  // Any vet booking opened for billing (booking card, Bookings page, reception queue): pull its consultation items too.
  useEffect(() => {
    if (encounterId && bookingId && encBooking.current && encBooking.current !== bookingId) { clearEncounter(); return; }
    if (!bookingId || encounterId || collectInvoice || bkEncChecked.current === bookingId) return;
    bkEncChecked.current = bookingId;
    (async () => {
      try {
        const r = (await api.get(`/veterinary/bookings/${bookingId}/encounter`)).data;
        if (r.status === "completed") {
          loadEncounter(r.encounter_id);
          if (r.others) toast.warning(`This booking has ${r.others + 1} completed consultations (one per pet). Only one is loaded here — bill the others from "Consultations awaiting billing".`, { duration: 10000 });
        }
        else if (r.encounter_id) toast.warning("The vet has not completed this consultation yet — medicines, vaccines and procedures will appear once it is completed.", { duration: 8000 });
      } catch {}
    })();
  }, [bookingId, encounterId, collectInvoice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link from the reception queue (?invoice=…): an existing unpaid invoice opens in collect mode.
  useEffect(() => {
    const iid = params.get("invoice");
    if (!iid) return;
    (async () => {
      try {
        const inv = (await api.get(`/invoices/${iid}`)).data;
        if (["paid", "cancelled", "void"].includes(inv.status) || !(Number(inv.balance) > 0)) { toast.info("This invoice has nothing left to collect."); return; }
        const c = (await api.get(`/customers/${inv.customer_id}`)).data;
        setCustomer(c); setSelectedPets(inv.pet_id ? [inv.pet_id] : []);
        if (inv.branch_id) setBranchId(inv.branch_id);
        setCollectInvoice(inv); setCollectLines([{ method: "upi", amount: inv.balance }]); collectIdem.current = uuid();
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, [params]);


  // Server-authoritative live preview (debounced)
  useEffect(() => {
    if (!customer || cart.length === 0) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pos/preview", {
          customer_id: customer.id, branch_id: branchId || "",
          discount: canDiscount ? Number(discount) || 0 : 0, coupon_code: coupon.trim(),
          booking_id: bookingId || "", cross_sell_standalone: !bookingId && csStandalone,
          tax_enabled: gstOn, tax_rate: gstOn && gstRate !== "" ? Number(gstRate) : null,
          items: cart.map((c) => c.kind === "custom"
          ? { kind: "custom", ref_id: "", name: c.name, qty: c.qty, price: Number(c.price) || 0, tax_rate: Number(c.tax_rate) || 0, pet_id: c.pet_id || "", discount_type: canDiscount ? (c.discount_type || "") : "", discount_value: canDiscount ? (Number(c.discount_value) || 0) : 0 }
          : { kind: c.kind, ref_id: c.ref_id, source_entry_id: c.source_entry_id || "", qty: c.qty, staff_id: c.staff_id || "", pet_id: c.pet_id || "", discount_type: canDiscount ? (c.discount_type || "") : "", discount_value: canDiscount ? (Number(c.discount_value) || 0) : 0 }),
        });
        setPreview(data);
        if (data.coupon_error) toast.error(`Coupon: ${data.coupon_error}`);
      } catch (e) { /* keep last preview */ }
    }, 300);
    return () => clearTimeout(t);
  }, [customer, cart, discount, coupon, branchId, canDiscount, gstOn, gstRate, bookingId, csStandalone]);

  // RECONCILE: whenever the authoritative selected booking loads or changes, rebuild only
  // the booking-derived cart lines (source:"booking"). Manual POS lines are preserved; lines
  // are rebuilt from scratch with stable keys so re-selection/edits never duplicate. This is
  // READ/SYNC-ONLY — it never writes back to the booking (no loop).
  useEffect(() => {
    if (!bookingId || !selBooking || selBooking.id !== bookingId) return;
    const svcRows = (selBooking.services && selBooking.services.length)
      ? selBooking.services
      : (selBooking.service_id
          ? [{ service_id: selBooking.service_id, service_name: selBooking.service_name, price: selBooking.price, tax: 0, qty: 1 }]
          : []);
    const svcLines = svcRows.filter((s) => s.service_id).map((s) => ({
      _k: `bk-svc-${s.pet_id || ""}-${s.service_id}`, kind: "service", ref_id: s.service_id, name: s.service_name,
      qty: Math.max(1, parseInt(s.qty, 10) || 1), pet_id: s.pet_id || "",
      price: s.price, tax_rate: s.price ? Math.round(((s.tax || 0) / s.price) * 100) : 18,
      staff_id: selBooking.staff_id || "", source: "booking", booking_id: bookingId,
    }));
    const prodLines = (selBooking.products || []).filter((p) => p.product_id).map((p) => ({
      _k: `bk-prd-${p.product_id}`, kind: "product", ref_id: p.product_id, name: p.name,
      qty: Math.max(1, parseInt(p.qty, 10) || 1),
      price: p.price, tax_rate: p.price ? Math.round(((p.tax || 0) / p.price) * 100) : 18,
      staff_id: "", source: "booking", booking_id: bookingId,
    }));
    const bkLines = [...svcLines, ...prodLines];
    setCart((prev) => [...bkLines, ...prev.filter((l) => l.source !== "booking")]);
  }, [bookingId, selBooking?.id, selBooking?.updated_at]);

  // Single-pet customers keep the simple one-pet behaviour (auto-selected). Pet selection is
  // cleared when switching customers (manual pick / new customer), so it never leaks across customers.
  useEffect(() => {
    if ((ctx?.pets || []).length === 1 && selectedPets.length === 0) setSelectedPets([ctx.pets[0].id]);
  }, [ctx?.pets]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = preview?.total ?? 0;
  const cash = cashBreakdown(payments, total);
  const paySum = cash.applied;
  const balanceDue = cash.balance;

  const addItem = (kind, item) => setCart((c) => [...c, {
    _k: uuid(), kind, ref_id: item.id, name: item.name, qty: 1,
    price: kind === "service" ? item.price : item.selling_price,
    tax_rate: kind === "service" ? item.tax_rate : item.gst, staff_id: "",
    pet_id: kind === "service" && selectedPets.length === 1 ? selectedPets[0] : "",
  }]);
  const removeItem = (k) => setCart((c) => c.filter((it) => it._k !== k));
  const patchItem = (k, patch) => setCart((c) => c.map((it) => it._k === k ? { ...it, ...patch } : it));

  const resetSale = () => {
    encLoaded.current = ""; bkEncChecked.current = "";
    setCustomer(null); setSelectedPets([]); setBookingId(""); setEncounterId(""); setEncNote(null); setCart([]); setDiscount(0); setDiscountReason("");
    setCoupon(""); setPayments([{ method: "cash", amount: "" }]); setSplitMode(false); setPreview(null);
    setGstOn(true); setGstRate(""); setCsStandalone(false);
    setCollectInvoice(null); setCollectLines([]);
    setCustQuery(""); idemRef.current = uuid();
  };

  const createCustomer = async () => {
    const mobile = normalizeMobile(newCust?.mobile);
    if (!newCust?.name || !mobile || addressError(newCust)) { setNewCust((c) => ({ ...c, touched: true })); return; }
    try {
      const { data } = await api.post("/customers", { name: newCust.name, mobile, branch_id: branchId || "",
        pin: newCust.pin, state: newCust.state, city: newCust.city, area: newCust.area, location_id: newCust.location_id, country: "India" });
      setCustomer(data); setSelectedPets([]); setNewCust(null); toast.success("Customer created");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const createPet = async () => {
    if (!newPet?.name) { toast.error("Pet name required"); return; }
    const he = petAgeError(newPet) || petHealthError(newPet);
    if (he) { toast.error(he); return; }
    try {
      const { data } = await api.post("/pets", { name: newPet.name, species: newPet.species || "dog", customer_id: customer.id, age_years: newPet.age_years ?? null, age_months: newPet.age_months ?? null, ...petHealthPayload(newPet) });
      setSelectedPets((s) => Array.from(new Set([...s, data.id]))); setNewPet(null); qc.invalidateQueries({ queryKey: ["pos-ctx", customer.id] }); toast.success("Pet added");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const updatePay = (i, patch) => setPayments((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addPayLine = () => setPayments((arr) => [...arr, { method: "cash", amount: "" }]);
  const removePayLine = (i) => setPayments((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));
  const fillRemaining = () => setPayments((arr) => {
    const others = arr.slice(0, -1).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const rem = Math.max(0, Number((total - others).toFixed(2)));
    return arr.map((x, idx) => (idx === arr.length - 1 ? { ...x, amount: rem } : x));
  });
  const toggleSplit = () => {
    const next = !splitMode;
    setSplitMode(next);
    if (!next) setPayments([{ method: payments[0]?.method || "cash", amount: payments[0]?.amount || "" }]);
    else if (payments.length === 0) setPayments([{ method: "cash", amount: "" }]);
  };

  const checkout = async () => {
    if (!customer) { toast.error("Select a customer"); return; }
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    // Payment is a SEPARATE step from creating the bill: an empty payment creates an UNPAID
    // invoice (collect later), and a fully package-covered ₹0 bill needs no payment at all.
    // Only a negative/invalid payment entry is rejected here.
    if (payments.some((p) => Number(p.amount) < 0)) { toast.error("Payment amount can't be negative"); return; }
    if (splitMode && payments.filter((p) => Number(p.amount) > 0).length === 0) { toast.error("Add at least one payment amount or switch to single payment"); return; }
    if (paySum > total + 0.01) { toast.error(`Payments (${inrFull(paySum)}) exceed the amount due (${inrFull(total)})`); return; }
    const today = localDay(new Date());
    const isBackdated = payDate && payDate < today;
    if (payDate && payDate > today) { toast.error("Payment date cannot be in the future"); return; }
    if (paySum > 0 && isBackdated && !backdateReason.trim()) { toast.error("A reason is required to backdate a payment"); return; }
    if (needSaleBranch()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/checkout", {
        customer_id: customer.id, pet_id: petId, branch_id: branchId || "", booking_id: bookingId || "", encounter_id: encounterId || "",
        discount: canDiscount ? Number(discount) || 0 : 0, discount_reason: discountReason,
        coupon_code: coupon.trim(), cross_sell_standalone: !bookingId && csStandalone,
        tax_enabled: gstOn, tax_rate: gstOn && gstRate !== "" ? Number(gstRate) : null,
        items: cart.map((c) => c.kind === "custom"
          ? { kind: "custom", ref_id: "", name: c.name, qty: c.qty, price: Number(c.price) || 0, tax_rate: Number(c.tax_rate) || 0, pet_id: c.pet_id || "", discount_type: canDiscount ? (c.discount_type || "") : "", discount_value: canDiscount ? (Number(c.discount_value) || 0) : 0 }
          : { kind: c.kind, ref_id: c.ref_id, source_entry_id: c.source_entry_id || "", qty: c.qty, staff_id: c.staff_id || "", pet_id: c.pet_id || "", discount_type: canDiscount ? (c.discount_type || "") : "", discount_value: canDiscount ? (Number(c.discount_value) || 0) : 0 }),
        payments: cash.lines.filter((p) => p.applied > 0).map((p) => ({ method: p.method, amount: p.applied, ...(p.method === "cash" ? { cash_received: p.received } : {}), reference: "", payment_date: payDate || "", backdate_reason: (payDate && payDate < new Date().toLocaleDateString("en-CA")) ? backdateReason.trim() : "" })),
      }, { headers: { "Idempotency-Key": idemRef.current } });
      setReceipt(data.receipt);
      toast.success(`${data.invoice.invoice_number} · ${data.invoice.status.replace("_", " ")}`);
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["today-bookings"] });
      qc.invalidateQueries({ queryKey: ["pos-ctx", customer.id] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const collectOutstanding = async (inv) => {
    const amt = inv.balance;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/collect", { invoice_id: inv.id, payments: [{ method: "cash", amount: amt }] });
      setReceipt(data.receipt);
      toast.success(`Collected ${inrFull(amt)} · ${inv.invoice_number}`);
      qc.invalidateQueries({ queryKey: ["pos-ctx", customer.id] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const collectBooking = async () => {
    const cc = cashBreakdown(collectLines, collectInvoice.balance);
    const lines = cc.lines.filter((l) => l.applied > 0);
    const amt = cc.applied;
    if (collectLines.some((l) => Number(l.amount) < 0)) { toast.error("Payment amount can't be negative"); return; }
    if (!(amt > 0)) { toast.error("Enter a payment amount"); return; }
    if (amt > Number(collectInvoice.balance) + 0.01) { toast.error(`Payments (${inrFull(amt)}) exceed the outstanding balance (${inrFull(collectInvoice.balance)})`); return; }
    const today = localDay(new Date());
    const backdated = payDate && payDate < today;
    if (payDate && payDate > today) { toast.error("Payment date cannot be in the future"); return; }
    if (backdated && !backdateReason.trim()) { toast.error("A reason is required to backdate a payment"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pos/collect", {
        invoice_id: collectInvoice.id,
        payments: lines.map((l) => ({ method: l.method, amount: l.applied, ...(l.method === "cash" ? { cash_received: l.received } : {}), reference: "", payment_date: payDate || "", backdate_reason: backdated ? backdateReason.trim() : "" })),
      }, { headers: { "Idempotency-Key": collectIdem.current } });
      setReceipt(data.receipt);
      toast.success(`Collected ${inrFull(amt)} · ${collectInvoice.invoice_number}`);
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["today-bookings"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  // Razorpay online payment. The amount/currency/order are 100% backend-authoritative
  // (POST /payments/create-order derives them from the invoice balance); the browser
  // only receives the public key_id + order_id and, on success, hands the signed
  // callback to POST /payments/verify for server-side signature verification + capture.
  const payOnline = async (inv, opts = {}) => {
    if (!inv || onlineBusy) return;
    setOnlineBusy(true);
    const fail = (msg) => { toast.error(msg); setOnlineBusy(false); };
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) return fail("Unable to start online payment. Please try again.");
      let order;
      try {
        order = (await api.post("/payments/create-order", { invoice_id: inv.id, provider: "razorpay" })).data;
      } catch (e) { return fail(apiErr(e) || "Unable to start online payment. Please try again."); }
      if (!order?.order_id || !order?.key_id) return fail("Online payment is not available right now.");
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency || "INR",
        order_id: order.order_id,
        name: user?.tenant_name || user?.business_name || "Pawkt",
        description: `Invoice ${order.invoice_number}`,
        prefill: { name: customer?.name || "", email: customer?.email || "", contact: customer?.mobile || "" },
        theme: { color: "#6A48F0" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/payments/verify", {
              invoice_id: inv.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Payment successful · ${inrFull(data.payment?.amount || order.amount)}`);
            qc.invalidateQueries({ queryKey: ["today-bookings"] });
            qc.invalidateQueries({ queryKey: ["dashboard"] });
            if (customer?.id) qc.invalidateQueries({ queryKey: ["pos-ctx", customer.id] });
            setCollectInvoice(null); setBookingId(null);
            opts.onSuccess?.();
          } catch (e) {
            toast.error("Payment verification failed. Please contact the business before retrying.");
          } finally { setOnlineBusy(false); }
        },
        modal: { ondismiss: () => { setOnlineBusy(false); toast.message("Payment cancelled."); }, escape: true },
      });
      rzp.on("payment.failed", () => { toast.error("Payment was not completed."); });
      rzp.open();
    } catch (e) {
      fail("Unable to start online payment. Please try again.");
    }
  };

  // POS walk-in Razorpay: create-order needs an existing invoice, so first create the
  // canonical bill via the EXISTING /pos/checkout (no payments → unpaid invoice), then hand
  // off to the EXISTING payOnline() gateway flow. Reuses the same create-order → Checkout →
  // verify → _apply_payment_captured path as the invoice/booking collect flows — no new system.
  const payOnlineFromCart = async () => {
    if (busy || onlineBusy) return;
    if (!customer) { toast.error("Select a customer"); return; }
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    if (!(total > 0)) { toast.error("Nothing to pay online"); return; }
    if (needSaleBranch()) return;
    setOnlineBusy(true);
    let inv;
    try {
      const { data } = await api.post("/pos/checkout", {
        customer_id: customer.id, pet_id: petId, branch_id: branchId || "", booking_id: bookingId || "", encounter_id: encounterId || "",
        discount: canDiscount ? Number(discount) || 0 : 0, discount_reason: discountReason,
        coupon_code: coupon.trim(), cross_sell_standalone: !bookingId && csStandalone,
        tax_enabled: gstOn, tax_rate: gstOn && gstRate !== "" ? Number(gstRate) : null,
        items: cart.map((c) => c.kind === "custom"
          ? { kind: "custom", ref_id: "", name: c.name, qty: c.qty, price: Number(c.price) || 0, tax_rate: Number(c.tax_rate) || 0, pet_id: c.pet_id || "", discount_type: canDiscount ? (c.discount_type || "") : "", discount_value: canDiscount ? (Number(c.discount_value) || 0) : 0 }
          : { kind: c.kind, ref_id: c.ref_id, source_entry_id: c.source_entry_id || "", qty: c.qty, staff_id: c.staff_id || "", pet_id: c.pet_id || "", discount_type: canDiscount ? (c.discount_type || "") : "", discount_value: canDiscount ? (Number(c.discount_value) || 0) : 0 }),
        payments: [],
      }, { headers: { "Idempotency-Key": idemRef.current } });
      inv = data.invoice;
      if (customer?.id) qc.invalidateQueries({ queryKey: ["pos-ctx", customer.id] });
    } catch (e) { setOnlineBusy(false); toast.error(apiErr(e)); return; }
    if (!inv?.id) { setOnlineBusy(false); toast.error("Could not create the bill."); return; }
    if (!(inv.balance > 0)) { setOnlineBusy(false); toast.error("This bill is fully covered — nothing to pay online."); return; }
    await payOnline(inv, { onSuccess: resetSale });
  };

  // Front-desk one-tap: open an appointment's bill. Not-billed -> create the ONE canonical
  // invoice (idempotent) then go straight to payment; already-billed -> collect the existing one.
  const openBookingBill = async (b) => {
    setGenBusy(b.id);
    try {
      let invId = b.billing?.invoice_id;
      if (!invId) {
        const { data } = await api.post(`/invoices/from-booking/${b.id}`);
        invId = data.id;
      }
      const inv = (await api.get(`/invoices/${invId}`)).data;
      const c = (await api.get(`/customers/${b.customer_id}`)).data;
      if (b.id !== bookingId) clearEncounter();
      setCustomer(c); setSelectedPets(b.pet_id ? [b.pet_id] : []); setBookingId(b.id);
      if (b.branch_id) setBranchId(b.branch_id);
      setCollectInvoice(inv); setCollectLines([{ method: "upi", amount: inv.balance }]); collectIdem.current = uuid();
      qc.invalidateQueries({ queryKey: ["today-bookings"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setGenBusy(null); }
  };

  // BILL REVIEW: clicking a NOT_BILLED booking card loads its services into the editable POS
  // cart (server-authoritative /pos/preview, add/remove/qty, package coverage) WITHOUT creating
  // an invoice. The canonical invoice is created only on Checkout (/pos/checkout, idempotent,
  // booking-linked). A booking is the operational record; the bill is confirmed by staff.
  const reviewBookingBill = async (b) => {
    setGenBusy(b.id);
    try {
      const c = (await api.get(`/customers/${b.customer_id}`)).data;
      setCollectInvoice(null);
      if (b.id !== bookingId) clearEncounter();
      setCustomer(c); setSelectedPets(b.pet_id ? [b.pet_id] : []);
      if (b.branch_id) setBranchId(b.branch_id);
      // Set identity only — the authoritative ["pos-booking", id] query + reconcile effect
      // hydrate the cart from the CURRENT booking (services + products). No stale snapshot.
      setBookingId(b.id);
      qc.invalidateQueries({ queryKey: ["pos-booking", b.id] });
      toast.success("Bill ready to review — add or remove services/products, then Checkout to create the invoice");
    } catch (e) { toast.error(apiErr(e)); } finally { setGenBusy(null); }
  };

  const activeToday = (todayBookings?.items || []).filter((b) => !["cancelled", "no_show"].includes(b.status));
  const isToday = apptDate === localDay(new Date());
  const earlierDue = (earlierBookings?.items || []).filter((b) => !b.is_series_master && !["cancelled", "no_show"].includes(b.status)
    && ["NOT_BILLED", "UNPAID", "PARTIALLY_PAID"].includes(b.billing?.state || "NOT_BILLED"));

  // Whole-card click → primary action for that billing state (inner buttons stopPropagation).
  const cardClick = (b, st) => {
    if (st === "NOT_BILLED") reviewBookingBill(b);
    else if (st === "UNPAID" || st === "PARTIALLY_PAID") openBookingBill(b);
  };

  return (
    <div>
      <PageHeader title="Point of Sale" subtitle="One screen: cart, discounts, split payment, receipt" />
      {collectInvoice && (
        <div className="rounded-xl border border-emerald-200 bg-white p-4 mb-4 max-w-xl" data-testid="pos-collect-mode">
          <div className="text-sm font-semibold text-slate-800 mb-2">Collect payment · {collectInvoice?.invoice_number || `Booking #${(bookingId || params.get("booking") || "").slice(-6)}`}</div>
          <div className="text-sm text-slate-600 space-y-0.5">
            <div>Customer: <b className="text-slate-900">{customer?.name}</b></div>
            <div>Invoice: <b className="text-slate-900">{collectInvoice.invoice_number}</b></div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 pt-1">
              <span>Total {inrFull(collectInvoice.total)}</span>
              <span>Paid {inrFull(collectInvoice.amount_paid)}</span>
              <span className="text-rose-600 font-semibold" data-testid="pos-collect-outstanding">Outstanding {inrFull(collectInvoice.balance)}</span>
            </div>
          </div>
          <CollectAllocation lines={collectLines} setLines={setCollectLines} balance={Number(collectInvoice.balance) || 0}
            methods={METHODS} labels={METHOD_LABELS} payDate={payDate} setPayDate={setPayDate}
            reason={backdateReason} setReason={setBackdateReason} canBackdate={canBackdate} />
          <div className="flex flex-wrap items-end gap-2 mt-3">
            <Button onClick={collectBooking} disabled={busy || onlineBusy} data-testid="pos-collect-booking" className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Collecting…" : "Collect Payment"}</Button>
            {razorpayOn && (
              <Button onClick={() => payOnline(collectInvoice)} disabled={busy || onlineBusy} data-testid="pos-pay-online"
                className="bg-pawkt-primary hover:bg-pawkt-primary-hover text-white">
                {onlineBusy ? "Starting…" : "Pay Online"}
              </Button>
            )}
            <DiscardInvoiceButton invoice={collectInvoice} label="Discard Invoice"
              onDone={() => { setCollectInvoice(null); setBookingId(null); qc.invalidateQueries({ queryKey: ["today-bookings"] }); }} />
          </div>
        </div>
      )}
      {!collectInvoice && (
        <div className="mb-5" data-testid="pos-today-queue">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-bold text-pawkt-text">{isToday ? "Today's appointments" : "Appointments"}</h2>
              <Input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value || localDay(new Date()))}
                aria-label="Appointments date" className="h-8 w-40 bg-white text-xs" data-testid="pos-appt-date" />
              {!isToday && <button type="button" onClick={() => setApptDate(localDay(new Date()))} className="text-xs text-emerald-700 hover:underline" data-testid="pos-appt-date-today">Back to today</button>}
            </div>
            <span className="text-xs text-pawkt-text-secondary">{activeToday.length} to bill</span>
          </div>
          {earlierDue.length > 0 && (
            <div className="mb-3 rounded-[18px] border border-amber-200 bg-amber-50/40 p-3" data-testid="pos-earlier-unpaid">
              <div className="mb-2 text-sm font-semibold text-amber-800">Earlier unpaid appointments <span className="font-normal text-amber-700">· last {BACKDATE_WINDOW_DAYS} days · {earlierDue.length}</span></div>
              <div className="divide-y divide-amber-100">
                {earlierDue.map((b) => {
                  const st = b.billing?.state || "NOT_BILLED";
                  const amt = st === "NOT_BILLED" ? estBookingAmount(b) : (b.billing?.balance || 0);
                  return (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`pos-earlier-${b.id}`}>
                      <div className="min-w-0 text-sm">
                        <span className="font-semibold text-slate-900">{b.customer_name || "—"}</span>
                        <span className="text-xs text-slate-500"> · {b.pet_name || ""} · {new Date(b.start_time).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })}{b.is_backdated ? " · backdated" : ""}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold tabular">{inr(amt)}</span>
                        {st === "NOT_BILLED" ? (
                          <Button size="sm" disabled={genBusy === b.id} onClick={() => reviewBookingBill(b)} data-testid={`pos-earlier-review-${b.id}`} className="h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-700">Review Bill</Button>
                        ) : (
                          <Button size="sm" disabled={genBusy === b.id} onClick={() => openBookingBill(b)} data-testid={`pos-earlier-collect-${b.id}`} className="h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-700">Collect {inr(b.billing?.balance)}</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {activeToday.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-pawkt-border bg-white p-6 text-center text-sm text-pawkt-text-secondary" data-testid="pos-today-empty">
              {isToday ? "No appointments to bill today." : "No appointments on this date."} Use the search below for a walk-in sale.
            </div>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
              {activeToday.map((b) => {
                const st = b.billing?.state || "NOT_BILLED";
                const petRoster = b.pets && b.pets.length ? b.pets : (b.pet_name ? [{ id: b.pet_id, name: b.pet_name }] : []);
                const multiPet = petRoster.length > 1;
                const svcRows = b.services || [];
                const units = svcRows.reduce((t, s) => t + (parseInt(s.qty, 10) || 1), 0);
                const svcNames = svcRows.map((s) => s.service_name).filter(Boolean).join(" + ") || b.service_name || "—";
                const amount = st === "NOT_BILLED" ? estBookingAmount(b) : (b.billing?.total || 0);
                return (
                  <div key={b.id} data-testid={`pos-appt-${b.id}`}
                    role="button" tabIndex={0}
                    onClick={() => cardClick(b, st)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); cardClick(b, st); } }}
                    className={`rounded-[18px] border border-pawkt-border bg-white p-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${st === "PAID" ? "" : "cursor-pointer hover:border-emerald-300 hover:shadow-sm"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{b.customer_name || "—"}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {multiPet
                            ? `${petRoster.length} pets · ${svcRows.length} services${units > svcRows.length ? ` · ${units} units` : ""}`
                            : (b.pet_name || "")}
                          {b.start_time ? ` · ${ftime(b.start_time)}` : ""}
                        </div>
                      </div>
                      {st === "PAID" && <span data-testid={`pos-appt-paid-${b.id}`} className="shrink-0 text-xs font-semibold text-emerald-700 bg-pawkt-mint px-2.5 py-1 rounded-full">Paid</span>}
                    </div>
                    {multiPet
                      ? <PetBreakdown booking={b} roster={petRoster} />
                      : <div className="mt-2 text-xs text-slate-600 line-clamp-2">{svcNames}</div>}
                    <div className="mt-3">
                      {st === "NOT_BILLED" ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-slate-900 text-sm tabular">{inr(amount)}</span>
                            <Button size="sm" disabled={genBusy === b.id} onClick={(e) => { e.stopPropagation(); reviewBookingBill(b); }} data-testid={`review-bill-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs gap-1">
                              <Receipt className="h-3.5 w-3.5" /> {genBusy === b.id ? "…" : "Review Bill"}
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditBk(b); }} data-testid={`edit-services-${b.id}`} className="h-11 text-xs gap-1 flex-1"><Pencil className="h-3 w-3" /> Edit Services</Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setCancelBk(b); }} data-testid={`cancel-appt-${b.id}`} className="h-11 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-slate-900 text-sm tabular">{inr(amount)}</span>
                          {(st === "UNPAID" || st === "PARTIALLY_PAID") && (
                            <Button size="sm" disabled={genBusy === b.id} onClick={(e) => { e.stopPropagation(); openBookingBill(b); }} data-testid={`pos-collect-appt-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs gap-1">
                              <IndianRupee className="h-3.5 w-3.5" /> {genBusy === b.id ? "…" : `Collect ${inr(b.billing?.balance)}`}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {customer && <ContextPanel open={!!panelCtx} onOpenChange={(o) => !o && setPanelCtx(false)} customerId={customer.id} petId={petId} vertical="reception" />}

      <div className={`grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-5${collectInvoice ? " hidden" : ""}`}>
        {/* LEFT: customer + catalog */}
        <div className="space-y-4">
          {/* Customer bar */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            {multiBranch && (
              <div className="mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                <Select value={branchId || ""} onValueChange={setBranchId}>
                  <SelectTrigger className={`h-8 w-56 bg-white ${branchId ? "" : "border-amber-400"}`} data-testid="pos-branch"><SelectValue placeholder="Choose sale branch" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`pos-branch-${b.id}`}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!branchId && <span className="text-xs text-amber-700" data-testid="pos-branch-required">Required for walk-in bills</span>}
              </div>
            )}

            {!customer ? (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="pos-customer-search" value={custQuery} onChange={(e) => setCustQuery(e.target.value)}
                  placeholder="Search customer by name / phone / email…" className="pl-9 bg-white" />
                {custQuery.length >= 2 && (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 max-h-60 overflow-auto">
                    {(custSearch?.items || []).map((c) => (
                      <button key={c.id} data-testid={`pos-cust-opt-${c.id}`} onClick={() => { setCustomer(c); setSelectedPets([]); setCustQuery(""); }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-emerald-50/50">
                        <span className="text-sm font-medium text-slate-800">{c.name}</span>
                        <span className="text-xs text-slate-400">{c.mobile}</span>
                      </button>
                    ))}
                    <button data-testid="pos-new-customer" onClick={() => setNewCust({ name: custQuery, mobile: "" })}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-emerald-700 hover:bg-emerald-50/50 text-sm">
                      <UserPlus className="h-4 w-4" /> Create new customer “{custQuery}”
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg font-semibold text-slate-900" data-testid="pos-selected-customer">{customer.name}</span>
                    <span className="text-sm text-slate-400">{customer.mobile}</span>
                    <button title="View context" data-testid="pos-ctx-open" onClick={() => setPanelCtx(true)} className="p-1 rounded-md hover:bg-sky-50 text-sky-600"><PawPrint className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {(ctx?.pets || []).length === 0 ? (
                      <span className="text-xs text-slate-400" data-testid="pos-no-pets">No pets registered for this customer</span>
                    ) : (ctx?.pets || []).length === 1 ? (
                      <Badge className="bg-sky-100 text-sky-800 gap-1" data-testid="pos-single-pet"><PawPrint className="h-3 w-3" />{ctx.pets[0].name}</Badge>
                    ) : (
                      <div className="flex flex-wrap items-center gap-1.5" data-testid="pos-pet-multiselect">
                        {(ctx?.pets || []).map((p) => {
                          const on = selectedPets.includes(p.id);
                          return (
                            <button key={p.id} type="button" data-testid={`pos-pet-opt-${p.id}`}
                              onClick={() => setSelectedPets((s) => on ? s.filter((x) => x !== p.id) : [...s, p.id])}
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors ${on ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"}`}>
                              {on ? <Check className="h-3 w-3" /> : <PawPrint className="h-3 w-3" />}{p.name}
                            </button>
                          );
                        })}
                        <button type="button" onClick={() => setSelectedPets((ctx?.pets || []).map((p) => p.id))} data-testid="pos-pets-all" className="text-xs font-semibold text-sky-700 hover:underline">Select all</button>
                        <button type="button" onClick={() => setSelectedPets([])} data-testid="pos-pets-clear" className="text-xs font-semibold text-slate-400 hover:underline">Clear</button>
                        <span className="text-xs text-slate-500" data-testid="pos-pets-count">{selectedPets.length} pet{selectedPets.length === 1 ? "" : "s"} selected</span>
                      </div>
                    )}
                    <button onClick={() => setNewPet({ name: "", species: "dog" })} className="text-xs text-emerald-700 hover:underline" data-testid="pos-new-pet">+ pet</button>
                    {(ctx?.memberships || []).map((m) => (
                      <Badge key={m.id} className="bg-amber-100 text-amber-800 gap-1"><Crown className="h-3 w-3" />{m.plan_name}{m.discount_pct ? ` · ${m.discount_pct}%` : ""}</Badge>
                    ))}
                    {(ctx?.packages || []).map((p) => (
                      <Badge key={p.id} className="bg-indigo-100 text-indigo-800 gap-1"><Layers className="h-3 w-3" />{p.plan_name} · {p.remaining_credits} {unitNoun(pkgCategory(p, svcCat), p.remaining_credits)} left</Badge>
                    ))}
                    {ctx?.outstanding_total > 0 && (
                      <Badge className="bg-rose-100 text-rose-700" data-testid="pos-outstanding-badge">Outstanding {inrFull(ctx.outstanding_total)}</Badge>
                    )}
                  </div>
                </div>
                <button onClick={resetSale} className="text-slate-400 hover:text-rose-500" data-testid="pos-clear-customer"><X className="h-4 w-4" /></button>
              </div>
            )}

            {/* Outstanding collection */}
            {customer && (ctx?.outstanding_invoices || []).length > 0 && (
              <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50/40 p-2.5">
                <div className="text-xs font-semibold text-rose-700 mb-1.5">Outstanding invoices</div>
                <div className="space-y-1">
                  {ctx.outstanding_invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between text-sm" data-testid={`pos-outstanding-${inv.id}`}>
                      <span className="text-slate-700">{inv.invoice_number} · <span className="text-slate-400">{inv.status.replace("_", " ")}</span></span>
                      <span className="flex items-center gap-2">
                        <span className="font-medium tabular">{inrFull(inv.balance)}</span>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={busy} onClick={() => collectOutstanding(inv)} data-testid={`pos-collect-${inv.id}`}>Collect</Button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Catalog */}
          <div className="mb-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input data-testid="pos-catalog-search" value={catQuery} onChange={(e) => setCatQuery(e.target.value)}
              placeholder="Filter services & products by name…" className="pl-9 bg-white" />
            {catQuery && <button onClick={() => setCatQuery("")} data-testid="pos-catalog-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>}
          </div>
          <Section title="Services" icon={Scissors}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(services || []).filter((s) => !catQuery || (s.name || "").toLowerCase().includes(catQuery.toLowerCase()) || (s.category || "").toLowerCase().includes(catQuery.toLowerCase())).map((s) => (
                <button key={s.id} data-testid={`pos-service-${s.id}`} onClick={() => addItem("service", s)}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/40">
                  <span className="text-sm font-medium text-slate-800 truncate">{s.name}</span>
                  <span className="text-sm font-semibold text-slate-900 ml-2">{inrFull(s.price)}</span>
                </button>
              ))}
            </div>
          </Section>
          <Section title="Products" icon={Package}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(products || []).filter((p) => !catQuery || (p.name || "").toLowerCase().includes(catQuery.toLowerCase()) || (p.sku || "").toLowerCase().includes(catQuery.toLowerCase())).map((p) => (
                <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addItem("product", p)} disabled={p.stock <= 0}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/40 disabled:opacity-40">
                  <span className="text-sm font-medium text-slate-800 truncate">{p.name} <span className="text-xs text-slate-400">· {p.stock} left</span></span>
                  <span className="text-sm font-semibold text-slate-900 ml-2">{inrFull(p.selling_price)}</span>
                </button>
              ))}
            </div>
          </Section>

          {canManual && (
            <button data-testid="pos-manual-charge-btn"
              onClick={() => setManual({ name: "", amount: "", tax_rate: (taxOptions?.rates || []).includes(18) ? 18 : ((taxOptions?.rates || [0])[0] ?? 0) })}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-emerald-400 hover:text-emerald-700">
              <Plus className="h-4 w-4" /> Add manual charge
            </button>
          )}
        </div>

        {/* RIGHT: cart + payment */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 h-fit xl:sticky xl:top-20">
          <div className="flex items-center gap-2 mb-3"><ShoppingCart className="h-5 w-5 text-slate-400" /><h3 className="font-display text-lg font-semibold">Cart</h3></div>
          <EncounterNotice note={encNote} onRefresh={encounterId ? () => loadEncounter(encounterId, true) : null} />

          <div className="divide-y divide-slate-100 border-y border-slate-100 max-h-72 overflow-auto">
            {cart.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Tap services or products to add</p>}
            {(bookingId ? [{ k: "booking", title: "Original booking items", items: cart.filter((c) => c.source === "booking") },
              ...(cart.some((c) => c.source === "encounter") ? [{ k: "vet", title: "Items from vet consultation", items: cart.filter((c) => c.source === "encounter") }] : []),
              { k: "addon", title: "Cross-sell / Add-on Sales", items: cart.filter((c) => c.source !== "booking" && c.source !== "encounter") }]
              : cart.some((c) => c.source === "encounter") ? [{ k: "vet", title: "Items from vet consultation", items: cart.filter((c) => c.source === "encounter") },
                { k: "all", title: "Other items", items: cart.filter((c) => c.source !== "encounter") }]
              : [{ k: "all", title: "", items: cart }]).map((g) => (
            <div key={g.k} data-testid={`pos-cart-group-${g.k}`}>
              {g.title && cart.length > 0 && <div className={`pt-2 text-[11px] font-extrabold uppercase tracking-wide ${g.k === "addon" ? "text-emerald-700" : "text-slate-500"}`}>{g.title}</div>}
              {g.k === "addon" && cart.length > 0 && !g.items.length && <p className="py-1.5 text-xs text-slate-400" data-testid="pos-addon-empty">Optional — search products or services on the left to add an add-on sale.</p>}
            {g.items.map((it) => {
              const pl = (preview?.lines || []).find((l) => l.ref_id === it.ref_id && l.kind === it.kind);
              const covered = pl?.covered_qty || 0;
              return (
                <div key={it._k} className="py-2" data-testid={`cart-item-${it.source_entry_id || it.ref_id}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{it.name}</div>
                      <div className="text-xs text-slate-400">{inrFull(it.price)} × {it.qty}{covered > 0 && <span className="text-indigo-600"> · {covered} by package</span>}</div>
                    </div>
                    <input type="number" min="1" value={it.qty} onChange={(e) => patchItem(it._k, { qty: Math.max(1, Number(e.target.value)) })}
                      className="w-12 rounded border border-slate-200 px-1.5 py-1 text-sm text-center" data-testid={`cart-qty-${it.ref_id}`} />
                    <button onClick={() => removeItem(it._k)} data-testid={`cart-remove-${it.ref_id}`} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {it.kind === "service" && (staff?.length || 0) > 0 && (
                    <Select value={it.staff_id || "__none__"} onValueChange={(v) => patchItem(it._k, { staff_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="mt-1 h-7 w-full bg-white text-xs" data-testid={`cart-staff-${it.ref_id}`}><SelectValue placeholder="Assign staff (commission)" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="__none__">No staff</SelectItem>
                        {(staff || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {it.kind !== "custom" && (ctx?.pets || []).length > 1 && (
                    <Select value={it.pet_id || "__none__"} onValueChange={(v) => patchItem(it._k, { pet_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="mt-1 h-7 w-full bg-white text-xs" data-testid={`cart-pet-${it.ref_id}`}><SelectValue placeholder="For which pet?" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="__none__">No specific pet</SelectItem>
                        {(selectedPets.length ? (ctx?.pets || []).filter((p) => selectedPets.includes(p.id)) : (ctx?.pets || [])).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {canDiscount && <CartLineDiscount it={it} pl={pl} patchItem={patchItem} />}
                </div>
              );
            })}
            </div>))}
          </div>
          <CrossSellSummary preview={preview} bookingId={bookingId} cartLen={cart.length} standalone={csStandalone} setStandalone={setCsStandalone} />

          {/* Discounts */}
          <div className="space-y-2 mt-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Coupon</span>
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="CODE" className="w-28 h-7 text-right bg-white uppercase" data-testid="pos-coupon" />
            </div>
            {canDiscount && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Manual discount</span>
                  <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-28 h-7 text-right bg-white" data-testid="pos-discount" />
                </div>
                {Number(discount) > 0 && (
                  <Input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} placeholder="Reason (recorded)" className="h-7 bg-white text-xs" data-testid="pos-discount-reason" />
                )}
              </>
            )}
          </div>

          {/* Package coverage preview — server-authoritative (POS preview response).
              Display-only; preview never consumes credits. */}
          {preview?.package_credits_used > 0 && (() => {
            const covLines = (preview?.lines || []).filter((l) => (l.covered_qty || 0) > 0);
            const pkgRemaining = (ctx?.packages || []).reduce((t, p) => t + Number(p.remaining_credits || 0), 0);
            const after = Math.max(0, pkgRemaining - (preview.package_credits_used || 0));
            const covCat = svcCat[(covLines[0] || {}).ref_id] || "";
            return (
              <div className="mt-3 rounded-[14px] p-3" style={{ background: "#EEE9FE", border: "1px solid #DDD3FB" }} data-testid="pos-coverage-preview">
                <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide mb-2" style={{ color: "#5837D8" }}>
                  <Layers className="h-3.5 w-3.5" /> Package coverage
                </div>
                <div className="space-y-1.5">
                  {covLines.map((l) => (
                    <div key={l.ref_id} className="text-[13px]" data-testid={`pos-coverage-line-${l.ref_id}`}>
                      <div className="flex items-center justify-between" style={{ color: "#1F1B18" }}>
                        <span className="font-semibold truncate pr-2">{l.name}</span>
                        <span className="tabular shrink-0">{l.qty} {unitNoun(svcCat[l.ref_id] || "", l.qty)} requested</span>
                      </div>
                      <div className="mt-0.5"><CoverageBadge qty={l.qty} coveredQty={l.covered_qty || 0} category={svcCat[l.ref_id] || ""} testid={`pos-coverage-badge-${l.ref_id}`} /></div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 flex items-center justify-between text-[12px] border-t" style={{ borderColor: "#DDD3FB", color: "#7B6D62" }}>
                  <span>Package balance after checkout</span>
                  <span className="font-bold" style={{ color: "#1F1B18" }} data-testid="pos-balance-after">{after} {unitNoun(covCat, after)}</span>
                </div>
              </div>
            );
          })()}

          {/* Totals */}
          <div className="space-y-1.5 mt-3 text-sm border-t border-slate-100 pt-3">
            <Line label="Subtotal" value={inrFull(preview?.subtotal || 0)} testid="pos-subtotal" />
            {preview?.item_discount > 0 && <Line label="Item discounts" value={`-${inrFull(preview.item_discount)}`} accent testid="pos-item-discount" />}
            {preview?.membership_discount > 0 && <Line label="Membership (services)" value={`-${inrFull(preview.membership_discount)}`} accent testid="pos-membership-discount" />}
            {preview?.coupon_discount > 0 && <Line label="Coupon" value={`-${inrFull(preview.coupon_discount)}`} accent testid="pos-coupon-discount" />}
            {preview?.manual_discount > 0 && <Line label="Manual discount" value={`-${inrFull(preview.manual_discount)}`} accent testid="pos-manual-discount" />}
            {preview?.package_credits_used > 0 && <Line label={`Package ${unitNoun(svcCat[((preview?.lines || []).find((l) => (l.covered_qty || 0) > 0) || {}).ref_id] || "", 2)} used`} value={String(preview.package_credits_used)} accent />}
            <div className="flex items-center justify-between py-1" data-testid="pos-gst-control">
              <div className="flex items-center gap-2">
                <Switch checked={gstOn} onCheckedChange={setGstOn} data-testid="pos-gst-toggle" />
                <span className="text-slate-600">Apply GST</span>
              </div>
              {gstOn && (
                <Select value={gstRate === "" ? "__catalog__" : String(gstRate)}
                        onValueChange={(v) => setGstRate(v === "__catalog__" ? "" : v)}>
                  <SelectTrigger className="h-8 w-36 bg-white text-xs" data-testid="pos-gst-rate"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__catalog__">Catalog rates</SelectItem>
                    {(taxOptions?.rates || []).map((r) => <SelectItem key={r} value={String(r)}>{r === 0 ? "0% (exempt)" : `${r}% GST`}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Line label="Tax (GST)" value={inrFull(preview?.tax || 0)} testid="pos-tax" />
            <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-1">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="font-display text-2xl font-bold text-slate-900" data-testid="pos-total">{inrFull(total)}</span>
            </div>
          </div>

          {/* Payment — two clear choices for staff: pay now (online) OR record money already received */}
          <div className="mt-4 space-y-3">
            <Label className="text-xs text-slate-500 block">Payment Method</Label>

            {/* Section 1 — Pay Online (reuses the existing Razorpay flow) */}
            {razorpayOn && total > 0 && (
              <>
                <div className="rounded-xl border border-pawkt-primary/30 bg-pawkt-primary/5 p-3" data-testid="pos-pay-online-section">
                  <div className="text-[13px] font-semibold text-slate-900">Pay Online</div>
                  <div className="text-xs text-slate-500 mb-2">Customer pays securely online</div>
                  <Button onClick={payOnlineFromCart} disabled={busy || onlineBusy || !customer || cart.length === 0}
                    data-testid="pos-cart-pay-online"
                    className="w-full bg-pawkt-primary hover:bg-pawkt-primary-hover text-white gap-2 py-3 text-[15px] font-semibold">
                    {onlineBusy ? "Starting…" : `Pay Online · ${inrFull(total)}`}
                  </Button>
                </div>
                <div className="relative text-center"><span className="bg-white px-2 text-[11px] uppercase tracking-wide text-slate-400">or</span><div className="absolute inset-x-0 top-1/2 -z-10 h-px bg-slate-200" /></div>
              </>
            )}

            {/* Section 2 — Record Payment (manual methods already received) */}
            <div className="rounded-xl border border-slate-200 p-3" data-testid="pos-record-payment-section">
              <div className="text-[13px] font-semibold text-slate-900">Record Payment</div>
              <div className="text-xs text-slate-500 mb-2">Payment has already been received</div>
              {!splitMode ? (
                <>
                  <div className="grid grid-cols-3 gap-1.5" data-testid="pos-record-methods">
                    {RECORD_METHODS.map((m) => (
                      <button key={m.value} onClick={() => setPayments([{ method: m.value, amount: payments[0]?.amount ?? "" }])}
                        data-testid={`pos-record-method-${m.value}`}
                        className={`rounded-lg border px-2 py-2 text-sm font-medium transition-colors ${payments[0]?.method === m.value ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <Label className="text-xs text-slate-500 shrink-0">{payments[0]?.method === "cash" ? "Cash Received ₹" : "Amount ₹"}</Label>
                    <Input type="number" value={payments[0]?.amount ?? ""} placeholder={`${total}`}
                      onChange={(e) => setPayments([{ method: payments[0]?.method || "cash", amount: e.target.value }])}
                      className="h-9 flex-1 text-right bg-white" data-testid="pos-record-amount" />
                    <button onClick={() => setPayments([{ method: payments[0]?.method || "cash", amount: total }])}
                      className="text-xs text-emerald-700 hover:underline shrink-0" data-testid="pos-record-full">Full</button>
                  </div>
                </>
              ) : (
                <div className="space-y-2" data-testid="pos-split-lines">
                  {payments.map((p, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`pos-split-line-${i}`}>
                      <Select value={p.method} onValueChange={(v) => updatePay(i, { method: v })}>
                        <SelectTrigger className="h-9 w-32 bg-white" data-testid={`pos-split-method-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{RECORD_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" value={p.amount} placeholder={p.method === "cash" ? "Cash received" : "0"} aria-label={p.method === "cash" ? "Cash received" : "Amount"} onChange={(e) => updatePay(i, { amount: e.target.value })}
                        className="h-9 flex-1 text-right bg-white" data-testid={`pos-split-amount-${i}`} />
                      <button onClick={() => removePayLine(i)} disabled={payments.length <= 1}
                        className="text-rose-500 hover:text-rose-700 shrink-0 p-1 disabled:opacity-30" data-testid={`pos-split-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <button onClick={addPayLine} className="text-xs font-medium text-emerald-700 hover:underline" data-testid="pos-split-add">+ Add payment method</button>
                    <button onClick={fillRemaining} className="text-xs text-emerald-700 hover:underline" data-testid="pos-split-fill">Fill remaining</button>
                  </div>
                </div>
              )}
              <div className="mt-2">
                <button onClick={toggleSplit} className="text-xs font-medium text-sky-700 hover:underline" data-testid="pos-split-toggle">
                  {splitMode ? "Use a single payment method" : "Split across multiple methods"}
                </button>
              </div>
              {paySum > 0 && (
                <div className="mt-1.5 text-xs flex justify-between" data-testid="pos-pay-summary">
                  <span className="text-slate-500">Allocated {inrFull(paySum)}{total > 0 ? ` of ${inrFull(total)}` : ""}</span>
                  <span data-testid="pos-pay-balance" className={paySum > total + 0.01 ? "text-rose-600" : (balanceDue > 0 ? "text-amber-600" : "text-emerald-600")}>
                    {paySum > total + 0.01 ? `Over by ${inrFull(paySum - total)}` : (balanceDue > 0 ? `Balance ${inrFull(balanceDue)}` : "Full payment")}
                  </span>
                </div>
              )}
              <CashChange cash={cash} total={total} />
              <div className="mt-2.5 flex items-center gap-2">
                <Label className="text-xs text-slate-500 shrink-0">Paid on</Label>
                <Input type="date" value={payDate} max={new Date().toLocaleDateString("en-CA")}
                  min={canBackdate ? undefined : new Date().toLocaleDateString("en-CA")}
                  onChange={(e) => setPayDate(e.target.value)} data-testid="pos-record-payment-date"
                  className="h-9 flex-1 bg-white" />
              </div>
              {payDate && payDate < new Date().toLocaleDateString("en-CA") && (
                <div className="mt-2" data-testid="pos-backdate-block">
                  <Label className="text-xs text-amber-700 mb-1 block">Reason for backdating <span className="text-rose-500">*</span></Label>
                  <Input value={backdateReason} onChange={(e) => setBackdateReason(e.target.value)}
                    placeholder="Why dated earlier than today?" data-testid="pos-backdate-reason" className="h-9 bg-white" />
                </div>
              )}
            </div>

            <Button onClick={checkout} disabled={busy || !customer || cart.length === 0} data-testid="pos-checkout"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 py-2.5">
              <Receipt className="h-4 w-4" /> {busy ? "Processing…" : (paySum > 0 ? `Record Payment · ${inrFull(paySum)}` : `Create Bill · ${inrFull(total)}`)}
            </Button>
          </div>
        </div>
      </div>

      {/* Manual charge dialog */}
      <Dialog open={!!manual} onOpenChange={(o) => !o && setManual(null)}>
        <DialogContent className="bg-white sm:max-w-sm">
          <DialogHeader><DialogTitle>Manual charge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Description</Label>
              <Input value={manual?.name || ""} onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
                placeholder="e.g. Special handling" className="bg-white" data-testid="manual-desc" /></div>
            <div><Label className="text-xs">Amount (₹)</Label>
              <Input type="number" min="0" value={manual?.amount ?? ""} onChange={(e) => setManual((m) => ({ ...m, amount: e.target.value }))}
                className="bg-white" data-testid="manual-amount" /></div>
            <div><Label className="text-xs">Tax</Label>
              <Select value={String(manual?.tax_rate ?? 0)} onValueChange={(v) => setManual((m) => ({ ...m, tax_rate: Number(v) }))}>
                <SelectTrigger className="bg-white" data-testid="manual-tax"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(taxOptions?.rates || [0]).map((r) => <SelectItem key={r} value={String(r)}>{r === 0 ? "Tax exempt (0%)" : `${r}% GST`}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <Button data-testid="manual-save" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                const amt = Number(manual?.amount);
                if (!manual?.name?.trim()) { toast.error("Description is required"); return; }
                if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
                setCart((c) => [...c, { _k: uuid(), kind: "custom", ref_id: "", name: manual.name.trim(), qty: 1, price: amt, tax_rate: Number(manual.tax_rate) || 0 }]);
                setManual(null);
              }}>Add to cart</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New customer dialog */}
      <Dialog open={!!newCust} onOpenChange={(o) => !o && setNewCust(null)}>
        <DialogContent className="bg-white sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={newCust?.name || ""} onChange={(e) => setNewCust((c) => ({ ...c, name: e.target.value }))} className="bg-white" data-testid="new-cust-name" /></div>
            <div><Label className="text-xs">Mobile</Label><Input inputMode="tel" value={newCust?.mobile || ""} onChange={(e) => setNewCust((c) => ({ ...c, mobile: e.target.value, touched: true }))} className="bg-white" data-testid="new-cust-mobile" />
              {newCust?.touched && mobileError(newCust?.mobile) && <p className="mt-1 text-xs text-rose-600" data-testid="new-cust-mobile-error">{mobileError(newCust?.mobile)}</p>}</div>
            <AddressPicker value={newCust || {}} testid="pos-addr" onChange={(p) => setNewCust((c) => ({ ...c, ...p }))} error={newCust?.touched ? addressError(newCust || {}) : ""} />
            <Button onClick={createCustomer} disabled={!newCust?.name?.trim() || !!mobileError(newCust?.mobile) || !!addressError(newCust || {})} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-cust-save">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New pet dialog */}
      <Dialog open={!!newPet} onOpenChange={(o) => !o && setNewPet(null)}>
        <DialogContent className="bg-white sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New pet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Pet name</Label><Input value={newPet?.name || ""} onChange={(e) => setNewPet((c) => ({ ...c, name: e.target.value }))} className="bg-white" data-testid="new-pet-name" /></div>
            <div><Label className="text-xs">Species</Label>
              <Select value={newPet?.species || "dog"} onValueChange={(v) => setNewPet((c) => ({ ...c, species: v }))}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["dog", "cat", "bird", "rabbit", "other"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <PetAgeField value={newPet || {}} testid="pos-pet-age" onChange={(p) => setNewPet((c) => ({ ...c, ...p }))} />
            <PetHealthFields pet={newPet || {}} testid="pos-pet-health" onChange={(p) => setNewPet((c) => ({ ...c, ...p }))} />
            <Button onClick={createPet} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-pet-save">Add pet</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={(o) => { if (!o) { setReceipt(null); resetSale(); } }}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> {receipt?.invoice?.status === "unpaid" ? "Bill created" : "Payment recorded"}</DialogTitle></DialogHeader>
          {receipt && <ReceiptView receipt={receipt} />}
        </DialogContent>
      </Dialog>
      {editBk && <EditServicesDialog booking={editBk} onClose={() => setEditBk(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["today-bookings"] }); qc.invalidateQueries({ queryKey: ["pos-booking"] }); setEditBk(null); }} />}
      {cancelBk && <CancelAppointmentDialog booking={cancelBk} onClose={() => setCancelBk(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["today-bookings"] }); qc.invalidateQueries({ queryKey: ["pos-booking"] }); setCancelBk(null); }} />}
    </div>
  );
}

const CANCEL_REASONS = ["Customer cancelled", "Customer changed plans", "Service no longer required", "Other"];

function PetBreakdown({ booking, roster }) {
  const [open, setOpen] = useState(false);
  const svcRows = booking.services || [];
  const nameOf = (pid) => (roster.find((p) => p.id === pid)?.name) || "Pet";
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} data-testid={`pos-pet-breakdown-toggle-${booking.id}`} aria-expanded={open}
        className="flex items-center gap-1 text-xs font-medium text-pawkt-primary">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {open ? "Hide" : "View"} pet breakdown
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`pos-pet-breakdown-${booking.id}`}>
          {roster.map((p) => {
            const rows = svcRows.filter((s) => s.pet_id === p.id);
            if (!rows.length) return null;
            return (
              <div key={p.id} data-testid={`pos-pet-group-${booking.id}-${p.id}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{p.name || nameOf(p.id)}</div>
                {rows.map((s) => (
                  <div key={s.service_id} className="flex items-center justify-between text-xs text-slate-600">
                    <span>{s.service_name} ×{parseInt(s.qty, 10) || 1}</span>
                    <span className="tabular">{inr((s.price || 0) * (parseInt(s.qty, 10) || 1))}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MultiPetEditServices({ booking, onClose, onDone }) {
  const { data: catalog } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const MAX_QTY = 99;
  const roster = (booking.pets && booking.pets.length) ? booking.pets : (booking.pet_id ? [{ id: booking.pet_id, name: booking.pet_name }] : []);
  const svcById = (id) => (catalog || []).find((x) => x.id === id);
  // state: { petId: [{service_id, qty}] } seeded from booking.services
  const [rows, setRows] = useState(() => {
    const m = {}; roster.forEach((p) => { m[p.id] = []; });
    (booking.services || []).forEach((s) => {
      const pid = s.pet_id || booking.pet_id;
      if (!m[pid]) m[pid] = [];
      m[pid].push({ service_id: s.service_id, qty: Math.max(1, parseInt(s.qty, 10) || 1), name: s.service_name, price: s.price || 0, tax: s.tax || 0 });
    });
    return m;
  });
  const [addFor, setAddFor] = useState(null);   // petId currently adding a service for
  const [busy, setBusy] = useState(false);
  const [bkUpdatedAt, setBkUpdatedAt] = useState(booking.updated_at);
  const rowsFor = (pid) => rows[pid] || [];
  const addSvc = (pid, sid) => setRows((prev) => {
    if ((prev[pid] || []).some((r) => r.service_id === sid)) return prev;
    const s = svcById(sid);
    const tax = s ? Math.round((s.price || 0) * (s.tax_rate ?? 18) / 100 * 100) / 100 : 0;
    return { ...prev, [pid]: [...(prev[pid] || []), { service_id: sid, qty: 1, name: s?.name, price: s?.price || 0, tax }] };
  });
  const removeSvc = (pid, sid) => setRows((prev) => ({ ...prev, [pid]: (prev[pid] || []).filter((r) => r.service_id !== sid) }));
  const setQty = (pid, sid, n) => setRows((prev) => ({ ...prev, [pid]: (prev[pid] || []).map((r) => r.service_id === sid ? { ...r, qty: Math.min(MAX_QTY, Math.max(1, n)) } : r) }));
  const total = roster.reduce((t, p) => t + rowsFor(p.id).reduce((s, r) => s + (r.price + r.tax) * r.qty, 0), 0);
  const allItems = roster.flatMap((p) => rowsFor(p.id).map((r) => ({ pet_id: p.id, service_id: r.service_id, qty: r.qty })));
  const submit = async () => {
    if (allItems.length === 0) { toast.error("Add at least one service, or cancel the appointment."); return; }
    setBusy(true);
    try {
      await api.patch(`/bookings/${booking.id}/services`, { items: allItems, expected_updated_at: bkUpdatedAt });
      toast.success("Booking updated"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-services-modal">
        <DialogHeader><DialogTitle className="font-display">Edit services · {booking.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {roster.map((p) => {
            const prows = rowsFor(p.id);
            const available = (catalog || []).filter((s) => s.active !== false && !prows.some((r) => r.service_id === s.id));
            return (
              <div key={p.id} className="rounded-[18px] border border-pawkt-border bg-pawkt-bg p-3" data-testid={`edit-pet-section-${p.id}`}>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 mb-2"><PawPrint className="h-4 w-4 text-pawkt-primary" /> {p.name || "Pet"}</div>
                {prows.map((r) => (
                  <div key={r.service_id} className="mb-1.5 flex items-center justify-between gap-2 rounded-xl border border-pawkt-border bg-white px-2.5 py-2" data-testid={`edit-svc-${p.id}-${r.service_id}`}>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{r.name} <span className="text-xs text-slate-400">· {inr(r.price)}</span></span>
                    <div className="flex items-center rounded-lg border border-pawkt-border bg-white shrink-0">
                      <button onClick={() => setQty(p.id, r.service_id, r.qty - 1)} disabled={r.qty <= 1} data-testid={`qty-dec-${p.id}-${r.service_id}`} className="h-9 w-9 flex items-center justify-center text-slate-600 disabled:text-slate-300"><Minus className="h-3.5 w-3.5" /></button>
                      <span data-testid={`qty-val-${p.id}-${r.service_id}`} className="min-w-[1.75rem] text-center text-sm font-semibold tabular">{r.qty}</span>
                      <button onClick={() => setQty(p.id, r.service_id, r.qty + 1)} disabled={r.qty >= MAX_QTY} data-testid={`qty-inc-${p.id}-${r.service_id}`} className="h-9 w-9 flex items-center justify-center text-slate-600 disabled:text-slate-300"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button onClick={() => removeSvc(p.id, r.service_id)} data-testid={`remove-svc-${p.id}-${r.service_id}`} aria-label="Remove service" className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {addFor === p.id ? (
                  <Select value="" onValueChange={(v) => { if (v) { addSvc(p.id, v); setAddFor(null); } }}>
                    <SelectTrigger className="bg-white h-11" data-testid={`add-svc-select-${p.id}`}><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {available.length === 0 ? <div className="px-2 py-3 text-xs text-slate-400">No more services</div>
                        : available.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {inr(s.price)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Button variant="outline" onClick={() => setAddFor(p.id)} data-testid={`add-service-btn-${p.id}`} className="w-full h-10 gap-1 border-dashed"><Plus className="h-4 w-4" /> Add Service</Button>
                )}
                {prows.length === 0 && <p className="mt-1.5 text-xs text-slate-400">No services for {p.name || "this pet"}.</p>}
              </div>
            );
          })}
        </div>
        <BookingProductsEditor booking={booking} onUpdatedAt={setBkUpdatedAt} />
        <div className="flex items-center justify-between pt-1 text-sm"><span className="text-slate-500">Services preview total</span><b className="text-slate-900" data-testid="edit-services-total">{inrFull(total)}</b></div>
        {allItems.length === 0 && <p className="text-xs text-rose-600" data-testid="edit-no-services">No services selected. Cancel the appointment instead.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-services-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || allItems.length === 0} data-testid="update-booking-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Updating…" : "Update Booking"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditServicesDialog({ booking, onClose, onDone }) {
  const isMultiPet = (booking.pets && booking.pets.length > 1) ||
    new Set((booking.services || []).map((s) => s.pet_id).filter(Boolean)).size > 1;
  return isMultiPet
    ? <MultiPetEditServices booking={booking} onClose={onClose} onDone={onDone} />
    : <SinglePetEditServices booking={booking} onClose={onClose} onDone={onDone} />;
}

function SinglePetEditServices({ booking, onClose, onDone }) {
  const { data: catalog } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const priceMap = useMemo(() => {
    const m = {};
    (booking.services || []).forEach((s) => { m[s.service_id] = { name: s.service_name, price: s.price || 0, tax: s.tax || 0 }; });
    (catalog || []).forEach((s) => { m[s.id] = { name: s.name, price: s.price || 0, tax: Math.round((s.price || 0) * (s.tax_rate ?? 18) / 100 * 100) / 100 }; });
    return m;
  }, [booking, catalog]);
  const [sel, setSel] = useState(() => new Set((booking.services || []).map((s) => s.service_id)));
  const [qtys, setQtys] = useState(() => { const q = {}; (booking.services || []).forEach((s) => { q[s.service_id] = Math.max(1, parseInt(s.qty, 10) || 1); }); return q; });
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [bkUpdatedAt, setBkUpdatedAt] = useState(booking.updated_at);
  const MAX_QTY = 99;
  const qtyOf = (id) => Math.max(1, qtys[id] || 1);
  const addService = (id) => { setSel((p) => new Set(p).add(id)); setQtys((p) => ({ ...p, [id]: p[id] || 1 })); setQ(""); };
  const removeService = (id) => setSel((p) => { const n = new Set(p); n.delete(id); return n; });
  const setQty = (id, n) => setQtys((p) => ({ ...p, [id]: Math.min(MAX_QTY, Math.max(1, n)) }));
  const total = [...sel].reduce((t, id) => t + (priceMap[id] ? (priceMap[id].price + priceMap[id].tax) * qtyOf(id) : 0), 0);
  const available = (catalog || [])
    .filter((s) => s.active !== false && !sel.has(s.id))
    .filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase()));
  const submit = async () => {
    if (sel.size === 0) { toast.error("No services selected. Cancel the appointment instead."); return; }
    setBusy(true);
    try {
      const quantities = {}; [...sel].forEach((id) => { quantities[id] = qtyOf(id); });
      await api.patch(`/bookings/${booking.id}/services`, { service_ids: [...sel], quantities, expected_updated_at: bkUpdatedAt });
      toast.success("Booking updated"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="edit-services-modal">
        <DialogHeader><DialogTitle className="font-display">Edit services · {booking.pet_name || booking.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Current services</p>
          {[...sel].length === 0 && <p className="text-xs text-slate-400">No services selected.</p>}
          {[...sel].map((id) => {
            const p = priceMap[id] || { name: id, price: 0, tax: 0 };
            const n = qtyOf(id);
            return (
              <div key={id} data-testid={`edit-svc-${id}`} className="rounded-xl border border-pawkt-primary bg-pawkt-primary-soft px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-900">{p.name}</span>
                  <button onClick={() => removeService(id)} data-testid={`remove-svc-${id}`} className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-slate-500">{inr(p.price)}<span className="text-slate-300">·</span>unit</div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center rounded-lg border border-pawkt-border bg-white">
                      <button onClick={() => setQty(id, n - 1)} disabled={n <= 1} data-testid={`qty-dec-${id}`} className="h-11 w-11 flex items-center justify-center text-slate-600 disabled:text-slate-300 disabled:cursor-not-allowed"><Minus className="h-4 w-4" /></button>
                      <span data-testid={`qty-val-${id}`} className="min-w-[2rem] text-center text-sm font-semibold text-slate-900 tabular">{n}</span>
                      <button onClick={() => setQty(id, n + 1)} disabled={n >= MAX_QTY} data-testid={`qty-inc-${id}`} className="h-11 w-11 flex items-center justify-center text-slate-600 disabled:text-slate-300 disabled:cursor-not-allowed"><Plus className="h-4 w-4" /></button>
                    </div>
                    <span data-testid={`line-total-${id}`} className="min-w-[4rem] text-right text-sm font-semibold text-slate-900">{inrFull((p.price + p.tax) * n)}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {!adding ? (
            <Button variant="outline" onClick={() => setAdding(true)} data-testid="add-service-btn" className="w-full h-11 gap-1 border-dashed"><Plus className="h-4 w-4" /> Add Service</Button>
          ) : (
            <div className="rounded-xl border border-pawkt-border p-2 space-y-2" data-testid="add-service-panel">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Available services</p>
                <button onClick={() => { setAdding(false); setQ(""); }} data-testid="add-service-close" className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" data-testid="add-service-search" className="bg-white pl-9 h-11" />
              </div>
              <div className="space-y-1.5 max-h-[32vh] overflow-y-auto">
                {available.length === 0 && <p className="text-xs text-slate-400 px-1 py-2" data-testid="add-service-empty">No services available to add.</p>}
                {available.map((s) => (
                  <button key={s.id} onClick={() => addService(s.id)} data-testid={`add-svc-${s.id}`}
                    className="w-full flex items-center justify-between rounded-xl border border-pawkt-border bg-white px-3 py-3 text-left min-h-[44px] hover:border-pawkt-primary transition-colors">
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-900"><Plus className="h-3.5 w-3.5 text-pawkt-primary" /> {s.name}</span>
                    <span className="text-sm text-slate-600">{inr(s.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <BookingProductsEditor booking={booking} onUpdatedAt={setBkUpdatedAt} />
        <div className="flex items-center justify-between pt-1 text-sm"><span className="text-slate-500">Services preview total</span><b className="text-slate-900" data-testid="edit-services-total">{inrFull(total)}</b></div>
        {sel.size === 0 && <p className="text-xs text-rose-600" data-testid="edit-no-services">No services selected. Cancel the appointment instead.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-services-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || sel.size === 0} data-testid="update-booking-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Updating…" : "Update Booking"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Booking-level products editor. Persists each change via PATCH /bookings/{id}/products
// (records intent only — NEVER touches inventory). Invalidates ["pos-booking"] so the POS
// cart auto-syncs. Reports the new updated_at up so the sibling services save keeps its CAS.
function BookingProductsEditor({ booking, onUpdatedAt }) {
  const qc = useQueryClient();
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const [rows, setRows] = useState(() => (booking.products || []).map((p) => ({ product_id: p.product_id, name: p.name, price: p.price || 0, qty: Math.max(1, parseInt(p.qty, 10) || 1) })));
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const MAX_QTY = 99;
  const persist = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/bookings/${booking.id}/products`, { items: next.map((r) => ({ product_id: r.product_id, qty: r.qty })) });
      setRows(next);
      if (data?.updated_at) onUpdatedAt?.(data.updated_at);
      qc.invalidateQueries({ queryKey: ["pos-booking"] });
      qc.invalidateQueries({ queryKey: ["today-bookings"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const add = (p) => { if (rows.some((r) => r.product_id === p.id)) return; persist([...rows, { product_id: p.id, name: p.name, price: p.selling_price || 0, qty: 1 }]); setAdding(false); setQ(""); };
  const remove = (pid) => persist(rows.filter((r) => r.product_id !== pid));
  const setQty = (pid, n) => persist(rows.map((r) => r.product_id === pid ? { ...r, qty: Math.min(MAX_QTY, Math.max(1, n)) } : r));
  const available = (products || []).filter((p) => p.active !== false && !rows.some((r) => r.product_id === p.id) && p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const prodTotal = rows.reduce((t, r) => { const p = (products || []).find((x) => x.id === r.product_id); const g = p ? (p.gst ?? 18) : 18; return t + r.price * r.qty * (1 + g / 100); }, 0);
  return (
    <div className="mt-2 border-t border-slate-100 pt-3 space-y-2" data-testid="booking-products">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Products</p>
      {rows.length === 0 && !adding && <p className="text-xs text-slate-400" data-testid="booking-products-empty">No products added.</p>}
      {rows.map((r) => (
        <div key={r.product_id} data-testid={`booking-product-${r.product_id}`} className="rounded-xl border border-pawkt-border bg-white px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{r.name} <span className="text-xs text-slate-400">· {inr(r.price)}</span></span>
            <div className="flex items-center rounded-lg border border-pawkt-border bg-white shrink-0">
              <button onClick={() => setQty(r.product_id, r.qty - 1)} disabled={busy || r.qty <= 1} data-testid={`booking-product-dec-${r.product_id}`} className="h-9 w-9 flex items-center justify-center text-slate-600 disabled:text-slate-300"><Minus className="h-3.5 w-3.5" /></button>
              <span data-testid={`booking-product-qty-${r.product_id}`} className="min-w-[1.75rem] text-center text-sm font-semibold tabular">{r.qty}</span>
              <button onClick={() => setQty(r.product_id, r.qty + 1)} disabled={busy || r.qty >= MAX_QTY} data-testid={`booking-product-inc-${r.product_id}`} className="h-9 w-9 flex items-center justify-center text-slate-600 disabled:text-slate-300"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            <button onClick={() => remove(r.product_id)} disabled={busy} data-testid={`booking-product-remove-${r.product_id}`} aria-label="Remove product" className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      {!adding ? (
        <Button variant="outline" onClick={() => setAdding(true)} disabled={busy} data-testid="add-product-btn" className="w-full h-11 gap-1 border-dashed"><Plus className="h-4 w-4" /> Add Product</Button>
      ) : (
        <div className="rounded-xl border border-pawkt-border p-2 space-y-2" data-testid="add-product-panel">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Available products</p>
            <button onClick={() => { setAdding(false); setQ(""); }} data-testid="add-product-close" className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="add-product-search" className="bg-white pl-9 h-11" />
          </div>
          <div className="space-y-1.5 max-h-[32vh] overflow-y-auto">
            {available.length === 0 && <p className="text-xs text-slate-400 px-1 py-2" data-testid="add-product-empty">No products available to add.</p>}
            {available.map((p) => (
              <button key={p.id} onClick={() => add(p)} disabled={busy} data-testid={`add-product-${p.id}`}
                className="w-full flex items-center justify-between rounded-xl border border-pawkt-border bg-white px-3 py-3 text-left min-h-[44px] hover:border-pawkt-primary transition-colors">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-900"><Plus className="h-3.5 w-3.5 text-pawkt-primary" /> {p.name} <span className="text-xs text-slate-400">· {p.stock} left</span></span>
                <span className="text-sm text-slate-600">{inr(p.selling_price)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <div className="flex items-center justify-between pt-1 text-sm" data-testid="booking-products-total">
          <span className="text-slate-500">Products subtotal</span>
          <b className="text-slate-900">{inrFull(prodTotal)}</b>
        </div>
      )}
    </div>
  );
}

function CartLineDiscount({ it, pl, patchItem }) {
  const [open, setOpen] = useState(false);
  const type = it.discount_type || "";
  const val = Number(it.discount_value) || 0;
  const amt = Number(pl?.discount_amount) || 0;
  const apply = (t, v) => patchItem(it._k, { discount_type: t, discount_value: v });
  return (
    <div className="mt-1" data-testid={`cart-disc-${it.ref_id}`}>
      {!open ? (
        val > 0 ? (
          <button onClick={() => setOpen(true)} data-testid={`cart-disc-edit-${it.ref_id}`} className="text-xs font-medium text-emerald-700 hover:underline">
            {type === "amount" ? "Fixed" : `${val}%`} OFF · -{inrFull(amt)} <span className="text-slate-400">(edit)</span>
          </button>
        ) : (
          <button onClick={() => setOpen(true)} data-testid={`cart-disc-add-${it.ref_id}`} className="text-xs font-medium text-slate-500 hover:text-slate-800 hover:underline">+ Add discount</button>
        )
      ) : (
        <div className="flex items-center gap-1.5 rounded-lg border border-pawkt-border bg-white p-1.5">
          <Select value={type || "percent"} onValueChange={(t) => apply(t, val)}>
            <SelectTrigger className="h-7 w-24 text-xs bg-white" data-testid={`cart-disc-type-${it.ref_id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="percent">Percent %</SelectItem>
              <SelectItem value="amount">Fixed ₹</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" min="0" value={val || ""} onChange={(e) => apply(type || "percent", Math.max(0, Number(e.target.value)))}
            placeholder={type === "amount" ? "₹" : "%"} className="h-7 w-16 text-right text-xs bg-white" data-testid={`cart-disc-value-${it.ref_id}`} />
          <span className="text-xs text-slate-500 flex-1 text-right" data-testid={`cart-disc-amt-${it.ref_id}`}>-{inrFull(amt)}</span>
          <button onClick={() => { apply("", 0); setOpen(false); }} data-testid={`cart-disc-clear-${it.ref_id}`} className="text-xs text-rose-600 px-1">Clear</button>
          <button onClick={() => setOpen(false)} data-testid={`cart-disc-done-${it.ref_id}`} className="text-xs font-medium text-slate-900 px-1">Done</button>
        </div>
      )}
    </div>
  );
}

function CancelAppointmentDialog({ booking, onClose, onDone }) {
  const [reason, setReason] = useState(CANCEL_REASONS[0]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${booking.id}/status`, { status: "cancelled", reason });
      toast.success("Appointment cancelled"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="cancel-appt-modal">
        <DialogHeader><DialogTitle className="font-display">Cancel this appointment?</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 space-y-0.5">
          <div>Customer: <b className="text-slate-900">{booking.customer_name}</b></div>
          <div>Pet: <b className="text-slate-900">{booking.pet_name}</b></div>
          <div className="text-xs text-slate-400">{(booking.services || []).map((s) => s.service_name).join(", ")}</div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="bg-white" data-testid="cancel-reason"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-appt-keep">Keep Appointment</Button>
          <Button onClick={submit} disabled={busy} data-testid="cancel-appt-submit" className="bg-rose-600 hover:bg-rose-700 text-white">{busy ? "Cancelling…" : "Cancel Appointment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptView({ receipt }) {
  const inv = receipt.invoice || {};
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const svcCat = Object.fromEntries((services || []).map((s) => [s.id, s.category]));
  const download = () => {
    const blob = new Blob([JSON.stringify(receipt, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${receipt.receipt_no}.json`; a.click();
  };
  return (
    <div>
      <div id="pos-receipt" className="rounded-lg border border-slate-200 p-4 text-sm" data-testid="pos-receipt">
        <div className="text-center border-b border-dashed border-slate-200 pb-2 mb-2">
          <div className="font-display text-lg font-bold">{receipt.business?.name}</div>
          {receipt.branch?.name && <div className="text-xs text-slate-400">{receipt.branch.name}</div>}
          <div className="text-xs text-slate-400">{receipt.receipt_no} · {inv.invoice_number}</div>
        </div>
        <div className="flex justify-between text-xs text-slate-500 mb-2">
          <span>{receipt.customer?.name}{receipt.pet ? ` · ${receipt.pet.name}` : ""}</span>
          <span>{receipt.served_by}</span>
        </div>
        <div className="space-y-1 border-b border-dashed border-slate-200 pb-2 mb-2" data-testid="receipt-items">
          {(() => {
            const its = inv.items || [];
            const petIds = [...new Set(its.map((it) => it.pet_id).filter(Boolean))];
            const multiPet = petIds.length > 1;
            const Line = (it, i) => (
              <div key={i} className="flex flex-col gap-0.5">
                <div className="flex justify-between"><span className="text-slate-700">{it.name} ×{it.qty}</span><span>{inrFull(it.line_total)}</span></div>
                {it.kind === "service" && it.ref_id && (it.covered_qty || 0) > 0 && (
                  <CoverageBadge qty={it.qty} coveredQty={it.covered_qty || 0} category={svcCat[it.ref_id] || ""} testid={`receipt-coverage-${i}`} />
                )}
              </div>
            );
            if (!multiPet) return its.map((it, i) => Line(it, i));
            return petIds.map((pid) => {
              const rows = its.filter((it) => it.pet_id === pid);
              const label = rows[0]?.pet_name || "Pet";
              return (
                <div key={pid} data-testid={`receipt-pet-${pid}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mt-1">{label}</div>
                  {rows.map((it, i) => Line(it, `${pid}-${i}`))}
                </div>
              );
            });
          })()}
        </div>
        <Row label="Subtotal" v={inv.subtotal} />
        {inv.membership_discount > 0 && <Row label="Membership" v={-inv.membership_discount} />}
        {inv.coupon_discount > 0 && <Row label="Coupon" v={-inv.coupon_discount} />}
        {inv.manual_discount > 0 && <Row label="Manual discount" v={-inv.manual_discount} />}
        <Row label="Tax" v={inv.tax} />
        <div className="flex justify-between font-semibold border-t border-slate-200 mt-1 pt-1"><span>Total</span><span>{inrFull(inv.total)}</span></div>
        <Row label="Paid" v={inv.amount_paid} />
        {inv.balance > 0 && <div className="flex justify-between text-amber-600"><span>Balance</span><span>{inrFull(inv.balance)}</span></div>}
        <div className="mt-2 text-xs text-slate-500">
          {(receipt.payments || []).map((p, i) => <div key={i} className="flex justify-between"><span className="uppercase">{p.method}</span><span>{inrFull(p.amount)}</span></div>)}
          <ReceiptCash payments={receipt.payments || []} />
        </div>
        <div className={`mt-2 text-center text-xs font-semibold uppercase ${inv.status === "paid" ? "text-emerald-600" : "text-amber-600"}`} data-testid="receipt-status">{(inv.status || "").replace("_", " ")}</div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" className="flex-1 gap-2" onClick={() => window.print()} data-testid="receipt-print"><Printer className="h-4 w-4" /> Print</Button>
        <Button variant="outline" className="flex-1 gap-2" onClick={download} data-testid="receipt-download"><Download className="h-4 w-4" /> Data</Button>
      </div>
      {inv.id && <div className="mt-3 border-t border-slate-100 pt-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Send invoice</div>
        <InvoiceActions invoice={{ ...inv, customer_name: receipt.customer?.name }} />
      </div>}
      <p className="mt-2 flex items-center gap-1 text-xs text-slate-400"><AlertCircle className="h-3 w-3" /> A receipt is queued to the customer via your configured channels.</p>
    </div>
  );
}

function CrossSellSummary({ preview, bookingId, cartLen, standalone, setStandalone }) {
  const cs = preview?.cross_sell;
  if (!cartLen) return null;
  return (
    <div className="mt-3 rounded-[14px] border border-emerald-200 bg-emerald-50/50 p-3 text-xs" data-testid="pos-cross-sell-summary">
      {!bookingId && (
        <label className="flex items-center gap-2 font-semibold text-slate-700">
          <Switch checked={standalone} onCheckedChange={setStandalone} data-testid="pos-cross-sell-standalone" /> Record as standalone add-on sale (no booking)
        </label>)}
      {cs && (bookingId || standalone) && (
        <div className="mt-1 space-y-1">
          {bookingId && <Row label="Original service subtotal" v={cs.original_subtotal} />}
          <Row label="Cross-sell subtotal (before tax)" v={cs.cross_sell_subtotal} />
          <Row label="Cross-sell tax" v={cs.cross_sell_tax} />
          <div className="flex justify-between font-bold text-emerald-800" data-testid="pos-cross-sell-total"><span>Total cross-sell (incl. tax)</span><span>{inrFull(cs.cross_sell_total || 0)}</span></div>
          <Row label="Final invoice total" v={preview?.total} />
        </div>)}
    </div>
  );
}
function Row({ label, v }) { return <div className="flex justify-between text-slate-600"><span>{label}</span><span>{inrFull(v || 0)}</span></div>; }

function ReceiptCash({ payments }) {
  const c = payments.filter((p) => p.method === "cash" && p.cash_received != null);
  if (!c.length) return null;
  const sum = (k) => c.reduce((s, p) => s + toP(p[k]), 0) / 100;
  return (
    <div className="mt-1 space-y-0.5 border-t border-dashed border-slate-200 pt-1" data-testid="receipt-cash">
      <div className="flex justify-between"><span>Cash received</span><span data-testid="receipt-cash-received">{inrFull(sum("cash_received"))}</span></div>
      <div className="flex justify-between"><span>Change returned</span><span data-testid="receipt-change-returned">{inrFull(sum("change_returned"))}</span></div>
      <div className="flex justify-between font-semibold text-slate-700"><span>Net cash applied</span><span data-testid="receipt-cash-applied">{inrFull(sum("amount"))}</span></div>
    </div>
  );
}
function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-slate-400" /><h3 className="font-display font-semibold text-slate-800">{title}</h3></div>
      {children}
    </div>
  );
}
function Line({ label, value, accent, testid }) {
  return <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className={accent ? "text-emerald-600 font-medium tabular" : "text-slate-800 tabular"} data-testid={testid}>{value}</span></div>;
}
